import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '@/modules/auth/hooks/useAuth';

/**
 * Callback page for OHD patient auto-login.
 *
 * The API redirects here after HMAC verification + session issuance,
 * passing the short-lived access token in the URL fragment (after `#`).
 * Fragments are not sent to servers, so the token doesn't leak into
 * upstream proxy logs or Referer headers. We consume it, bootstrap the
 * auth context, scrub the URL, and navigate to the patient dashboard.
 */
const OhdLaunch: React.FC = () => {
    const {login, isInitialized} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const token = useMemo(() => {
        const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
        const params = new URLSearchParams(hash);
        return params.get('token');
    }, [location.hash]);

    useEffect(() => {
        if (!isInitialized) return;

        if (!token) {
            setError('Missing launch token.');
            return;
        }

        try {
            const payloadB64 = token.split('.')[1];
            if (!payloadB64) {
                setError('Invalid token format.');
                return;
            }
            const payload = JSON.parse(atob(payloadB64));

            login({
                token,
                username: payload.sub,
                isLoggedIn: true,
                userInfo: {
                    id: payload.uid,
                    email: payload.sub,
                    organization_id: payload.organization_id,
                    provider_id: payload.provider_id,
                    role_id: payload.role_id,
                },
            });

            // Scrub the token out of the address bar before navigating away
            // so a curious user can't copy it from history once we're on /dashboard.
            window.history.replaceState(null, '', window.location.pathname);
            navigate('/dashboard', {replace: true});
        } catch {
            setError('Unable to launch the patient dashboard. Please try again.');
        }
    }, [isInitialized, token, login, navigate]);

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-mld-background px-6 text-center text-sm text-mld-text-primary">
                {error}
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-mld-background px-6 text-center text-sm text-mld-text-primary">
            Launching patient dashboard...
        </div>
    );
};

export default OhdLaunch;
